import React, { useState, useEffect } from 'react';
import { Link, Eye, EyeOff, Download, BarChart3, Copy, ExternalLink } from 'lucide-react';

const PublicLink = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const [visibility, setVisibility] = useState(initialData.visibility || 'private');
  const [password, setPassword] = useState(initialData.password || '');
  const [embargoDate, setEmbargoDate] = useState(initialData.embargoDate || '');
  const [embargoTime, setEmbargoTime] = useState(initialData.embargoTime || '');
  const [timezone, setTimezone] = useState(initialData.timezone || 'UTC');
  const [expiryDate, setExpiryDate] = useState(initialData.expiryDate || '');
  const [allowDownload, setAllowDownload] = useState(initialData.allowDownload || true);
  const [watermarkViewer, setWatermarkViewer] = useState(initialData.watermarkViewer || false);
  const [trackAnalytics, setTrackAnalytics] = useState(initialData.trackAnalytics || true);
  const [urlSlug, setUrlSlug] = useState(initialData.urlSlug || '');
  const [linkCreated, setLinkCreated] = useState(initialData.linkCreated || false);
  const [linkInfo, setLinkInfo] = useState(initialData.linkInfo || null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    // Load initial data when component mounts
  }, [initialData]);

  const generateUrlSlug = () => {
    const year = new Date().getFullYear();
    const company = 'company'; // This would come from company profile
    setUrlSlug(`${year}-${company}`);
  };

  const createPublicLink = async () => {
    setIsCreating(true);
    
    // Simulate link creation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const linkData = {
      url: `https://reports.example.com/${urlSlug || '2024-company'}`,
      status: 'active',
      createdAt: new Date().toISOString(),
      settings: {
        visibility,
        password: visibility === 'password' ? password : null,
        embargoDate: embargoDate ? `${embargoDate}T${embargoTime}:00.000Z` : null,
        expiryDate,
        allowDownload,
        watermarkViewer,
        trackAnalytics
      }
    };
    
    setLinkInfo(linkData);
    setLinkCreated(true);
    setIsCreating(false);
  };

  const copyToClipboard = () => {
    if (linkInfo) {
      navigator.clipboard.writeText(linkInfo.url);
    }
  };

  const previewLink = () => {
    if (linkInfo) {
      window.open(linkInfo.url, '_blank');
    }
  };


  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Public Link Configuration</h2>
        <p className="text-gray-600">Configure distribution link for your report</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration */}
        <div className="space-y-6">
          {/* Visibility */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Visibility</h3>
            <div className="space-y-3">
              {[
                { value: 'private', label: 'Private (default)', icon: EyeOff },
                { value: 'public', label: 'Public with link', icon: Eye },
                { value: 'password', label: 'Public + password', icon: Link }
              ].map(option => {
                const IconComponent = option.icon;
                return (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      name="visibility"
                      value={option.value}
                      checked={visibility === option.value}
                      onChange={(e) => setVisibility(e.target.value)}
                      className="mr-2"
                    />
                    <IconComponent className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Password */}
          {visibility === 'password' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Set access password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Embargo Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Embargo</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Publish at date/time
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={embargoDate}
                    onChange={(e) => setEmbargoDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <input
                    type="time"
                    value={embargoTime}
                    onChange={(e) => setEmbargoTime(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="UTC">UTC</option>
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                </select>
              </div>
            </div>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auto-disable link on date (optional)
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* URL Slug */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                URL slug
              </label>
              <button
                onClick={generateUrlSlug}
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                Auto-generate
              </button>
            </div>
            <input
              type="text"
              value={urlSlug}
              onChange={(e) => setUrlSlug(e.target.value)}
              placeholder="/reports/{year}-{company}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Full URL: https://reports.example.com/{urlSlug || '{slug}'}
            </p>
          </div>
        </div>

        {/* Right Column - Controls & Preview */}
        <div className="space-y-6">
          {/* Controls */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Controls</h3>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={allowDownload}
                onChange={(e) => setAllowDownload(e.target.checked)}
                className="mr-2"
              />
              <Download className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-700">Allow PDF download</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={watermarkViewer}
                onChange={(e) => setWatermarkViewer(e.target.checked)}
                className="mr-2"
              />
              <Eye className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-700">Watermark viewer email</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={trackAnalytics}
                onChange={(e) => setTrackAnalytics(e.target.checked)}
                className="mr-2"
              />
              <BarChart3 className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-700">Track opens/downloads (aggregate)</span>
            </label>
          </div>

          {/* Create Link Button */}
          <div className="text-center">
            <button
              onClick={createPublicLink}
              disabled={isCreating}
              className={`flex items-center justify-center w-full px-6 py-4 rounded-md transition-colors ${
                isCreating
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Link className="w-5 h-5 mr-2" />
              {isCreating ? 'Creating Link...' : 'Create/Update Public Link'}
            </button>
          </div>

          {/* Link Info */}
          {linkCreated && linkInfo && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-green-800">Link Created Successfully</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={copyToClipboard}
                    className="p-1 text-green-600 hover:text-green-700"
                    title="Copy URL"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={previewLink}
                    className="p-1 text-green-600 hover:text-green-700"
                    title="Preview link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-sm text-green-700">
                <p><strong>URL:</strong> {linkInfo.url}</p>
                <p><strong>Status:</strong> {linkInfo.status}</p>
                <p><strong>Created:</strong> {new Date(linkInfo.createdAt).toLocaleString()}</p>
                {linkInfo.settings.embargoDate && (
                  <p><strong>Embargo:</strong> {new Date(linkInfo.settings.embargoDate).toLocaleString()}</p>
                )}
              </div>
            </div>
          )}

          {/* Settings Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3">Configuration Summary</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Visibility:</strong> {visibility}</p>
              {visibility === 'password' && <p><strong>Password Protected:</strong> Yes</p>}
              <p><strong>Download:</strong> {allowDownload ? 'Allowed' : 'Disabled'}</p>
              <p><strong>Analytics:</strong> {trackAnalytics ? 'Enabled' : 'Disabled'}</p>
              {embargoDate && <p><strong>Embargo:</strong> {embargoDate} {embargoTime}</p>}
              {expiryDate && <p><strong>Expires:</strong> {expiryDate}</p>}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PublicLink;
